/***************************************************************************/
"use client";
/***************************************************************************/

import { VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../../utils/css";

const flexVariants = cva("flex", {
  variants: {
    direction: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
    "direction-sm": {
      horizontal: "sm:flex-row",
      vertical: "sm:flex-col",
    },
    "direction-md": {
      horizontal: "md:flex-row",
      vertical: "md:flex-col",
    },
    "direction-lg": {
      horizontal: "lg:flex-row",
      vertical: "lg:flex-col",
    },
    "direction-xl": {
      horizontal: "xl:flex-row",
      vertical: "xl:flex-col",
    },
    wrap: {
      wrap: "flex-wrap",
      nowrap: "flex-nowrap",
      reverse: "flex-wrap-reverse",
    },
    gap: {
      none: "gap-0",
      normal: "gap-4",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-5",
      xl: "gap-6",
    },
    "justify-content": {
      normal: "justify-normal",
      start: "justify-start",
      end: "justify-end",
      center: "justify-center",
      between: "justify-between",
      evenly: "justify-evenly",
      around: "justify-around",
      stretch: "justify-stretch",
    },
    "justify-content-sm": {
      normal: "sm:justify-normal",
      start: "sm:justify-start",
      end: "sm:justify-end",
      center: "sm:justify-center",
      between: "sm:justify-between",
      evenly: "sm:justify-evenly",
      around: "sm:justify-around",
      stretch: "sm:justify-stretch",
    },
    "justify-content-md": {
      normal: "md:justify-normal",
      start: "md:justify-start",
      end: "md:justify-end",
      center: "md:justify-center",
      between: "md:justify-between",
      evenly: "md:justify-evenly",
      around: "md:justify-around",
      stretch: "md:justify-stretch",
    },
    "justify-content-lg": {
      normal: "lg:justify-normal",
      start: "lg:justify-start",
      end: "lg:justify-end",
      center: "lg:justify-center",
      between: "lg:justify-between",
      evenly: "lg:justify-evenly",
      around: "lg:justify-around",
      stretch: "lg:justify-stretch",
    },
    "justify-content-xl": {
      normal: "xl:justify-normal",
      start: "xl:justify-start",
      end: "xl:justify-end",
      center: "xl:justify-center",
      between: "xl:justify-between",
      evenly: "xl:justify-evenly",
      around: "xl:justify-around",
      stretch: "xl:justify-stretch",
    },
    "align-items": {
      start: "items-start",
      end: "items-end",
      center: "items-center",
      baseline: "items-baseline",
      stretch: "items-stretch",
    },
    "align-items-sm": {
      start: "sm:items-start",
      end: "sm:items-end",
      center: "sm:items-center",
      baseline: "sm:items-baseline",
      stretch: "sm:items-stretch",
    },
    "align-items-md": {
      start: "md:items-start",
      end: "md:items-end",
      center: "md:items-center",
      baseline: "md:items-baseline",
      stretch: "md:items-stretch",
    },
    "align-items-lg": {
      start: "lg:items-start",
      end: "lg:items-end",
      center: "lg:items-center",
      baseline: "lg:items-baseline",
      stretch: "lg:items-stretch",
    },
    "align-items-xl": {
      start: "xl:items-start",
      end: "xl:items-end",
      center: "xl:items-center",
      baseline: "xl:items-baseline",
      stretch: "xl:items-stretch",
    },
  },
  defaultVariants: {
    direction: "horizontal",
    gap: "none",
  },
});

export type TFlexProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof flexVariants>;

const Flex: React.FC<TFlexProps> = ({
  className,
  direction,
  "direction-sm": directionSM,
  "direction-md": directionMD,
  "direction-lg": directionLG,
  "direction-xl": directionXL,
  "justify-content": justifyContent,
  "justify-content-sm": justifyContentSM,
  "justify-content-md": justifyContentMD,
  "justify-content-lg": justifyContentLG,
  "justify-content-xl": justifyContentXL,
  "align-items": alignItems,
  "align-items-sm": alignItemsSM,
  "align-items-md": alignItemsMD,
  "align-items-lg": alignItemsLG,
  "align-items-xl": alignItemsXL,
  gap,
  wrap,
  ...props
}) => {
  return (
    <div
      className={cn(
        flexVariants({
          direction,
          "direction-sm": directionSM,
          "direction-md": directionMD,
          "direction-lg": directionLG,
          "direction-xl": directionXL,
          "justify-content": justifyContent,
          "justify-content-sm": justifyContentSM,
          "justify-content-md": justifyContentMD,
          "justify-content-lg": justifyContentLG,
          "justify-content-xl": justifyContentXL,
          "align-items": alignItems,
          "align-items-sm": alignItemsSM,
          "align-items-md": alignItemsMD,
          "align-items-lg": alignItemsLG,
          "align-items-xl": alignItemsXL,
          gap,
          wrap,
        }),
        className,
      )}
      {...props}
    />
  );
};

export { Flex, flexVariants };
